import './ResizablePanel.css';

import {
  CSSProperties,
  FC,
  PointerEvent,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  LAYOUT_PANEL_INITIAL_WIDTH_RATIO,
  LAYOUT_PANEL_MIN_WIDTH,
  LAYOUT_WORKSPACE_MIN_WIDTH,
} from '../../../../../constants';
import { getLayoutPanelFormColumns } from '../../../../../utils/layouts/getLayoutPanelFormColumns';

interface ResizablePanelProps {
  children: ReactNode;
}

const ResizablePanel: FC<ResizablePanelProps> = ({ children }) => {
  const [panelWidth, setPanelWidth] = useState(LAYOUT_PANEL_MIN_WIDTH);
  const panelRef = useRef<HTMLElement>(null);
  // Pointer position and panel width captured on resize start; null = not resizing.
  const resizeStartRef = useRef<{ x: number; width: number } | null>(null);

  // Clamps a width between the panel minimum and "editor width minus min workspace".
  const clampPanelWidth = (width: number): number => {
    const editorWidth =
      panelRef.current?.parentElement?.clientWidth ?? window.innerWidth;
    const maxWidth = Math.max(
      LAYOUT_PANEL_MIN_WIDTH,
      editorWidth - LAYOUT_WORKSPACE_MIN_WIDTH
    );

    return Math.min(Math.max(width, LAYOUT_PANEL_MIN_WIDTH), maxWidth);
  };

  const handleResizeStart = (event: PointerEvent<HTMLDivElement>): void => {
    resizeStartRef.current = { x: event.clientX, width: panelWidth };
    // Capture keeps move/up events on the handle even when the pointer leaves it.
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizeMove = (event: PointerEvent<HTMLDivElement>): void => {
    const start = resizeStartRef.current;

    if (!start) {
      return;
    }

    // Dragging left widens the panel.
    setPanelWidth(clampPanelWidth(start.width + (start.x - event.clientX)));
  };

  const handleResizeEnd = (): void => {
    resizeStartRef.current = null;
  };

  // Initial width = fraction of the editor content width, measured on mount.
  // The editor may mount with zero width (route transition back from the
  // fullscreen reference) — then wait for its first real layout. Later editor
  // resizes (browser shrink) re-clamp the width to keep the workspace minimum.
  useLayoutEffect(() => {
    const editor = panelRef.current?.parentElement;

    if (!editor) {
      return;
    }

    let hasInitialWidth = false;

    const applyWidth = (): void => {
      if (editor.clientWidth <= 0) {
        return;
      }

      if (!hasInitialWidth) {
        hasInitialWidth = true;
        setPanelWidth(
          clampPanelWidth(editor.clientWidth * LAYOUT_PANEL_INITIAL_WIDTH_RATIO)
        );
        return;
      }

      setPanelWidth((current) => clampPanelWidth(current));
    };

    applyWidth();

    const observer = new ResizeObserver(applyWidth);
    observer.observe(editor);

    return () => observer.disconnect();
  }, []);

  return (
    <aside
      className="layout-editor-panel"
      ref={panelRef}
      style={
        {
          width: panelWidth,
          ['--form-columns']: getLayoutPanelFormColumns(panelWidth),
        } as CSSProperties
      }
    >
      <div
        className="layout-editor-panel-resize-handle"
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeEnd}
        onPointerCancel={handleResizeEnd}
      />
      {/* Scrolling lives on the inner wrapper so the absolutely positioned
          resize handle covers the panel's whole visible height. */}
      <div className="layout-editor-panel-content">{children}</div>
    </aside>
  );
};

export default ResizablePanel;
